import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from 'react';
import { TableCellDisplay } from './table/TableCellDisplay';
import { isAlwaysVisibleEditor } from './table/tableCellFormat';

export type ColumnType =
  | 'text'
  | 'single_select'
  | 'multi_select'
  | 'date'
  | 'person';

export interface ColumnOption {
  value: string;
  label: string;
}

export interface Column {
  key: string;
  label: string;
  type: ColumnType;
  /** Options for single_select / multi_select / person types. */
  options?: ColumnOption[];
  /** Optional width hint (CSS value). */
  width?: string;
  /**
   * When false, clicking the cell does not open an editor. If the table has
   * onRowOpen, a non-editable cell click invokes it (so Documents-style "click
   * the Title to open the editor" works without per-page custom wiring).
   * Defaults to true — existing consumers that don't set this keep their
   * current behavior.
   */
  editable?: boolean;
  /**
   * Commit policy for text + number editors. 'enter' = only Enter commits;
   * blur cancels. 'blur' = blur commits (the legacy behavior). Defaults to
   * 'blur'.
   */
  commitOn?: 'enter' | 'blur';
}

export type CellValue = string | string[] | number | null | undefined;

export interface Row {
  id: string;
  [key: string]: CellValue | string;
}

export type SortDirection = 'asc' | 'desc';

export interface SortDescriptor {
  columnKey: string;
  direction: SortDirection;
}

export interface BulkAction {
  id: string;
  label: string;
}

export interface UrlState {
  sort?: SortDescriptor;
  cols?: string[];
  page?: number;
}

export interface TableUrlStateAdapter {
  read: () => UrlState;
  write: (s: UrlState) => void;
}

export interface TableProps {
  columns: Column[];
  rows: Row[];
  defaultSort: SortDescriptor;
  onCellEdit: (
    rowId: string,
    columnKey: string,
    newValue: CellValue
  ) => Promise<void>;
  onBulkAction: (rowIds: string[], action: BulkAction) => Promise<void>;
  bulkActions?: BulkAction[];
  columnPickerEnabled?: boolean;
  multiSelectEnabled?: boolean;
  urlState?: TableUrlStateAdapter;
  /** Optional test-id prefix; defaults to `table`. */
  dataTestId?: string;
  /**
   * When set, the table renders a trailing "Open" column with a button per row.
   * Clicking it invokes the callback with the row id. The column sits outside
   * the inline-edit cell flow, so existing per-cell editors keep working.
   */
  onRowOpen?: (rowId: string) => void;
  /** Label for the per-row Open button. Defaults to "Open". */
  rowOpenLabel?: string;
}

const DEFAULT_BULK_ACTIONS: BulkAction[] = [
  { id: 'archive', label: 'Archive' }
];

function compareValues(a: CellValue, b: CellValue): number {
  const av = Array.isArray(a) ? a.join(',') : a;
  const bv = Array.isArray(b) ? b.join(',') : b;
  if (av == null && bv == null) return 0;
  if (av == null) return -1;
  if (bv == null) return 1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv));
}

export function Table({
  columns,
  rows,
  defaultSort,
  onCellEdit,
  onBulkAction,
  bulkActions = DEFAULT_BULK_ACTIONS,
  columnPickerEnabled = false,
  multiSelectEnabled = false,
  urlState,
  dataTestId = 'table',
  onRowOpen,
  rowOpenLabel = 'Open'
}: TableProps) {
  const tid = (suffix: string) => `${dataTestId}-${suffix}`;

  const initial = useMemo<UrlState>(() => urlState?.read() ?? {}, [urlState]);

  const [sort, setSort] = useState<SortDescriptor>(initial.sort ?? defaultSort);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(
    initial.cols && initial.cols.length > 0
      ? initial.cols
      : columns.map((c) => c.key)
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<
    { rowId: string; columnKey: string } | null
  >(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lastClickedRef = useRef<string | null>(null);
  const shiftKeyRef = useRef(false);

  useEffect(() => {
    urlState?.write({ sort, cols: visibleKeys });
  }, [sort, visibleKeys, urlState]);

  const visibleColumns = useMemo(() => {
    const matched = visibleKeys
      .map((k) => columns.find((c) => c.key === k))
      .filter((c): c is Column => c != null);
    // Foreign visibleKeys (e.g. another surface's keys leaked via a shared
    // `?cols=` URL param) match nothing. Never render a headerless table —
    // fall back to all columns.
    return matched.length > 0 ? matched : columns;
  }, [visibleKeys, columns]);

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.key === sort.columnKey);
    if (!col) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const cmp = compareValues(a[col.key] as CellValue, b[col.key] as CellValue);
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, sort, columns]);

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev.columnKey === key
        ? { columnKey: key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { columnKey: key, direction: 'asc' }
    );
  };

  const toggleColumn = (key: string) => {
    setVisibleKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      const next = columns
        .map((c) => c.key)
        .filter((k) => prev.includes(k) || k === key);
      return next;
    });
  };

  const handleRowCheck = useCallback(
    (rowId: string, shiftKey: boolean) => {
      const anchor = lastClickedRef.current;
      lastClickedRef.current = rowId;
      setSelectedIds((prev) => {
        if (shiftKey && anchor && anchor !== rowId) {
          const ids = sortedRows.map((r) => r.id);
          const a = ids.indexOf(anchor);
          const b = ids.indexOf(rowId);
          if (a !== -1 && b !== -1) {
            const [lo, hi] = a < b ? [a, b] : [b, a];
            const range = ids.slice(lo, hi + 1);
            return Array.from(new Set([...prev, ...range]));
          }
        }
        return prev.includes(rowId)
          ? prev.filter((id) => id !== rowId)
          : [...prev, rowId];
      });
    },
    [sortedRows]
  );

  const clearSelection = () => setSelectedIds([]);

  const runBulkAction = async (action: BulkAction) => {
    const ids = [...selectedIds];
    await onBulkAction(ids, action);
    clearSelection();
  };

  const commitEdit = async (
    rowId: string,
    columnKey: string,
    newValue: CellValue,
    clearEditing: boolean
  ) => {
    if (clearEditing) setEditing(null);
    await onCellEdit(rowId, columnKey, newValue);
  };

  return (
    <div className="pn-table-wrap" data-testid={tid('root')}>
      {columnPickerEnabled ? (
        <div className="pn-table-toolbar">
          <button
            type="button"
            className="pn-btn"
            data-testid={tid('column-picker-btn')}
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((v) => !v)}
          >
            Columns
          </button>
          {pickerOpen ? (
            <div
              className="pn-table-column-picker"
              role="menu"
              data-testid={tid('column-picker-menu')}
            >
              {columns.map((c) => {
                const checked = visibleKeys.includes(c.key);
                return (
                  <label
                    key={c.key}
                    className="pn-table-column-picker-item"
                    data-testid={tid(`column-picker-item-${c.key}`)}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleColumn(c.key)}
                      data-testid={tid(`column-picker-toggle-${c.key}`)}
                    />
                    <span>{c.label}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="pn-table-scroll" data-testid={tid('scroll')}>
      <table
        className="pn-table"
        data-testid={tid('grid')}
        data-row-count={sortedRows.length}
      >
        <thead>
          <tr>
            {multiSelectEnabled ? (
              <th className="pn-table-checkbox-col" scope="col">
                <span className="pn-visually-hidden">Select</span>
              </th>
            ) : null}
            {visibleColumns.map((c) => {
              const isSorted = sort.columnKey === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? ({ maxWidth: c.width } as CSSProperties) : undefined}
                  data-testid={tid(`header-${c.key}`)}
                  data-sort={isSorted ? sort.direction : 'none'}
                >
                  <button
                    type="button"
                    className="pn-table-header-btn"
                    onClick={() => toggleSort(c.key)}
                  >
                    <span>{c.label}</span>
                    <span aria-hidden="true" className="pn-table-sort-glyph">
                      {isSorted ? (sort.direction === 'asc' ? '↑' : '↓') : ''}
                    </span>
                  </button>
                </th>
              );
            })}
            {onRowOpen ? (
              <th
                className="pn-table-open-col"
                scope="col"
                data-testid={tid('header-open')}
              >
                <span className="pn-visually-hidden">{rowOpenLabel}</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const isChecked = selectedIds.includes(row.id);
            return (
              <tr
                key={row.id}
                data-testid={tid(`row-${row.id}`)}
                data-selected={isChecked ? 'true' : 'false'}
              >
                {multiSelectEnabled ? (
                  <td className="pn-table-checkbox-col">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      data-testid={tid(`row-checkbox-${row.id}`)}
                      onMouseDown={(e) => {
                        shiftKeyRef.current = e.shiftKey;
                      }}
                      onChange={() => {
                        handleRowCheck(row.id, shiftKeyRef.current);
                      }}
                    />
                  </td>
                ) : null}
                {visibleColumns.map((c) => {
                  const value = row[c.key] as CellValue;
                  const isEditing =
                    editing?.rowId === row.id && editing?.columnKey === c.key;
                  const alwaysVisible = isAlwaysVisibleEditor(c);
                  return (
                    <td
                      key={c.key}
                      data-testid={tid(`cell-${row.id}-${c.key}`)}
                      data-column-type={c.type}
                      data-editable={c.editable === false ? 'false' : 'true'}
                      style={c.width ? ({ maxWidth: c.width } as CSSProperties) : undefined}
                      onClick={() => {
                        if (alwaysVisible) return;
                        if (isEditing) return;
                        if (c.editable === false) {
                          if (onRowOpen) onRowOpen(row.id);
                          return;
                        }
                        setEditing({ rowId: row.id, columnKey: c.key });
                      }}
                    >
                      <TableCellDisplay
                        column={c}
                        value={value}
                        rowId={row.id}
                        editTestId={tid(`cell-edit-${row.id}-${c.key}`)}
                        isEditing={isEditing}
                        onStartEdit={() =>
                          setEditing({ rowId: row.id, columnKey: c.key })
                        }
                        onCommit={(v) =>
                          void commitEdit(row.id, c.key, v, !alwaysVisible)
                        }
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  );
                })}
                {onRowOpen ? (
                  <td className="pn-table-open-col">
                    <button
                      type="button"
                      className="pn-btn pn-table-open-btn"
                      data-testid={tid(`row-open-${row.id}`)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowOpen(row.id);
                      }}
                    >
                      {rowOpenLabel}
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {multiSelectEnabled && selectedIds.length > 0 ? (
        <div
          className="pn-table-bulk-bar"
          role="region"
          aria-label="Bulk actions"
          data-testid={tid('bulk-bar')}
        >
          <span data-testid={tid('bulk-count')}>
            {selectedIds.length} selected
          </span>
          <div className="pn-table-bulk-actions">
            {bulkActions.map((a) => (
              <button
                key={a.id}
                type="button"
                className="pn-btn"
                data-testid={tid(`bulk-action-${a.id}`)}
                onClick={() => {
                  void runBulkAction(a);
                }}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              className="pn-btn"
              data-testid={tid('bulk-clear')}
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
